import axios from 'axios'

class UsageService {
  constructor(apiUrl) {
    this.apiUrl = apiUrl
  }

  /**
   * @author Jorge Perales
   * @description Get plan info, avg tokens per conversation, daily average tokens and usage tokens by month
   * @param {String} clientId 
   * @param {String} year 
   * @param {String} month 
   * @param {String} email 
   * @param {String} token 
   * @returns {Object}
   * @memberof UsageService
   */
  async getUsagePart1Passthrough(year, month, token, botId = null) {
    const query = `year=${year}&month=${month}${botId ? `&botId=${botId}` : ''}`

    let response = await axios.get(`${this.apiUrl}/usage-part1-passthrough?${query}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    if (response.data.statusCode !== 200) {
      throw new Error(response.data.message)
    }
    return response.data.data
  }

  /**
   * @description Sentiment analysis report (traffic-light) for the usage/reports sections.
   * Grouped by day within the given month; the x-timezone header controls day bucketing (same as
   * getConversationsByMonth). Keep the returned shape stable — the charts depend on it.
   * The backend no longer returns the `gray` ("Sin datos") daily bucket nor the `not_required`
   * ("No requiere") attention status; both were dropped from the report on purpose.
   * @param {Number|String} year
   * @param {Number|String} month
   * @param {String} token
   * @param {String} botId
   * @returns {Promise<{ daily: Array<{date:string, green:number, yellow:number, red:number}>, attention: {attended:number, not_attended:number, resolved:number}, requiresReviewTotal:number }>}
   * @memberof UsageService
   */
  async getSentimentAnalysisReport(year, month, token, botId) {
    let userTimezone
    try {
      userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (error) {
      userTimezone = 'America/Mexico_City'
    }

    const query = `year=${year}&month=${month}${botId ? `&botId=${botId}` : ''}`
    const response = await axios.get(`${this.apiUrl}/sentiment-report?${query}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Timezone': userTimezone
      }
    })
    if (response.data.statusCode !== 200) {
      throw new Error(response.data.message)
    }
    return response.data.data
  }

  /**
   * @author Jorge Perales
   * @description Get conversations with messages by botId. Each conversation carries a normalized
   * `status` (lean) object: { value, isResolved, requiresReview, trafficLight, analysisState,
   * resolutionConfirmed } instead of the old loose keys.
   * @updatedDate 22-02-2026
   * @param {String} token
   * @param {String} botId
   * @returns {Object}
   * @memberof UsageService
   */
  async getConversationsByMonth(token, botId) {
    let userTimezone
    try {
        userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (error) {
        userTimezone = 'America/Mexico_City'
    }

    let response = await axios.get(`${this.apiUrl}/conversations-with-messages?botId=${botId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Timezone': userTimezone
      }
    })
    if (response.data.statusCode !== 200) {
      throw new Error(response.data.message)
    }
    return response.data.data
  }

  /**
   * @author Jorge Perales
   * @description Get a single conversation by conversationId with the same format as getConversationsByMonth
   * @date 18-05-2026
   * @param {String} conversationId
   * @param {String} token
   * @returns {Object}
   * @memberof UsageService
   */
  async getConversationById(conversationId, token) {
    let userTimezone
    try {
      userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (error) {
      userTimezone = 'America/Mexico_City'
    }

    let response = await axios.get(`${this.apiUrl}/conversation-by-id?conversationId=${conversationId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Timezone': userTimezone
      }
    })
    if (response.data.statusCode !== 200) {
      throw new Error(response.data.message)
    }
    return response.data.data
  }

  /**
   * @author Jorge Perales
   * @description Get daily token usage by clientId, year and month
   * @param {String} year 
   * @param {String} month 
   * @param {String} botId
   * @param {String} token 
   * @returns {Object}
   * @memberof UsageService
   */
  async getDailyTokenUsage(year, month, botId, token) {
    const query = `year=${year}&month=${month}${botId ? `&botId=${botId}` : ''}`
    let response = await axios.get(`${this.apiUrl}/daily-token-usage?${query}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    if (response.data.statusCode !== 200) {
      throw new Error(response.data.message)
    }
    return response.data.data
  }

  /**
   * @author Antonio Vazquez
   * @description method to update messages to seen
   * @date 06-11-2025
   * @param {string} conversationId | conversation id
   * @param {string} author | author prefix
   * @returns void
   * @memberof UsageService
   */
  async messagesSeen(conversationId, author) {
      try {
          const response = await axios.put(
              `${this.apiUrl}/messages-seen`,
              { conversationId, author }
          )

          if (response.data.statusCode !== 200) {
              throw new Error(response.data.message)
          }

          return response.data.data
      } catch (e) {
          console.error(`An error occurred in messagesSeen: ${e}`)
      }
    }

    /**
     * @author Antonio Vazquez
     * @description get conversation asigned to user
     * @createdDate 07-01-26
     * @param {*} userId
     * @param {*} token
     * @return {*}
     * @memberof UsageService
     */
    async getChatsByUserId(userId, token) {
      try {
        const response = await axios.get(
          `${this.apiUrl}/get-chats-by-userId?userId=${userId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        )

        if (response.data.statusCode !== 200) {
          throw new Error(response.data.message)
        }
        return response.data.data
      } catch (e) {
        console.error(`An error occurred in getChatsByUserId: ${e.message}`)
      }
    }

    /**
     * @author Antonio Vazquez
     * @description get categories and users to assign the conversation
     * @date 20-01-26
     * @param {*} clientId client id
     * @param {*} token token
     * @return {Object}
     * @memberof UsageService
     */
    async getCategoriesUsers(clientId, token) {
      try {
        const response = await axios.get(
          `${this.apiUrl}/categories-users?clientId=${clientId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        )

        if (response.data.statusCode !== 200) {
          throw new Error(response.data.message)
        }
        return response.data.data
      } catch (e) {
        console.error(`An error occurred in getCategoriesUsers: ${e.message}`)
      }
    }

    /**
     * @author Antonio Vazquez
     * @description assign conversation to a category or user
     * @date 20-01-26
     * @param {*} conversationId conversation id
     * @param {*} categoryId category id
     * @param {*} userId user id
     * @param {*} token token
     * @return {Object}
     * @memberof UsageService
     */
    async putAssignConversation(conversationId, categoryId, userId, token) {
      try {
        const response = await axios.put(
          `${this.apiUrl}/assign-conversation`,
          {
            conversationId,
            categoryId,
            userId
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        )

        if (response.data.statusCode !== 200) {
          throw new Error(response.data.message)
        }
        return response.data.data
      } catch (e) {
        console.error(`An error occurred in getCategoriesUsers: ${e.message}`)
      }
    }
}

export { UsageService as default }